/**
 * Feedback Metadata Utilities
 *
 * Utility functions for reading and writing feedback state to message metadata.
 * Follows the existing @assistant-ui/react message metadata pattern used for sessionId.
 */

import type {
  FeedbackType,
  FeedbackMessageMetadata,
  MessageWithFeedbackMetadata,
} from '../types/feedback';
import { MESSAGE_ROLES } from '../constants';
import { SessionManager } from './sessionManager';

/**
 * Reads feedback state from message metadata
 * @param message The message object with metadata
 * @returns The feedback state or null if not found
 */
export const getFeedbackState = (
  message: MessageWithFeedbackMetadata | any,
) => {
  return message?.metadata?.custom?.feedbackState || null;
};

/**
 * Checks if a message has feedback selected
 * @param message The message object with metadata
 * @returns True if feedback is selected, false otherwise
 */
export const hasFeedbackSelected = (
  message: MessageWithFeedbackMetadata | any,
): boolean => {
  const feedbackState = getFeedbackState(message);
  return feedbackState?.selected !== undefined;
};

/**
 * Checks if feedback is completed for a message
 * @param message The message object with metadata
 * @returns True if feedback process is completed
 */
export const isFeedbackCompleted = (
  message: MessageWithFeedbackMetadata | any,
): boolean => {
  const feedbackState = getFeedbackState(message);
  return feedbackState?.completed === true;
};

/**
 * Checks if message should show feedback buttons
 * New logic: show buttons if message is last OR has feedback selected
 * BUT exclude thank you messages, feedback intercepted messages, error messages,
 * and messages from previous sessions (archive context)
 * @param message The message to check
 * @param activeFeedbackMessageId Optional: messageId with active feedback from Redux
 * @returns True if buttons should be shown
 */
export const shouldShowFeedbackButtons = (
  message: MessageWithFeedbackMetadata | any,
  activeFeedbackMessageId?: string,
): boolean => {
  if (message?.role !== MESSAGE_ROLES.ASSISTANT) {
    return false;
  }

  // Never show buttons on thank you messages - check metadata flag
  if (message?.metadata?.custom?.isThankYouMessage === true) {
    return false;
  }

  // Also never show buttons on feedback intercepted messages
  if (message?.metadata?.custom?.feedbackIntercepted) {
    return false;
  }

  // Never show buttons on error messages - check status for error indicators
  if (message?.status?.error || message?.status?.reason === 'error') {
    return false;
  }

  // IMPORTANT: Check for feedback selected BEFORE session check
  // Messages with feedback already selected should ALWAYS show buttons
  // even after session reset (this preserves button state across sessions)
  const messageFeedbackSelected =
    hasFeedbackSelected(message) ||
    (activeFeedbackMessageId && message.id === activeFeedbackMessageId);

  if (messageFeedbackSelected) {
    return true;
  }

  // Never show buttons on messages from previous sessions (archive context)
  // This prevents feedback on messages after session reset
  // BUT we already returned true above if feedback was selected
  const messageSessionId = message?.metadata?.custom?.sessionId;
  const currentSessionId = SessionManager.getCurrentSessionId();

  if (
    messageSessionId &&
    currentSessionId &&
    messageSessionId !== currentSessionId
  ) {
    return false;
  }

  // Show buttons for last message (if not a thank you message or error message)
  return message.isLast === true;
};

/**
 * Checks if feedback buttons should be interactive (clickable)
 * Only last message should have interactive buttons
 * @param message The message to check
 * @returns True if buttons should be interactive
 */
export const shouldButtonsBeInteractive = (
  message: MessageWithFeedbackMetadata | any,
): boolean => {
  if (message?.role !== MESSAGE_ROLES.ASSISTANT) {
    return false;
  }

  // Only last message has interactive buttons, and only if not completed
  return message.isLast === true && !isFeedbackCompleted(message);
};
